import { ROLE_ITEMS, userSatisfiesRole } from '@/helpers/userSatisfiesRole';
import type { AllowedRole } from '@/helpers/userSatisfiesRole';
import type { AccessPolicy } from '@/access';
import { LOGIN_PATH } from '@/helpers/authenticationPaths';

// ─── types ──────────────────────────────────────────────────────────────────

export type RouteHandle = {
  access?: AccessPolicy;
  [key: string]: unknown;
};

export type RouteMatch = {
  handle?: RouteHandle;
  pathname: string;
};

type User = {
  isAdmin?: boolean;
  isSalesAdmin?: boolean;
  isPartner?: boolean;
  restrictedModules?: string[];
};

// ─── helpers ────────────────────────────────────────────────────────────────

export function isModuleRestricted(
  user: User,
  _routePath: string,
  allowedConfig: AccessPolicy['allowedConfig'] | undefined,
): boolean {
  if (!allowedConfig?.module) return false;
  return (
    !!user.restrictedModules?.length &&
    user.restrictedModules.includes(allowedConfig.module)
  );
}

function getAccessFlagRedirect({
  accessFlag,
  user,
  match,
  allowedConfig,
}: {
  accessFlag: string;
  user: User | null | undefined;
  match: RouteMatch;
  allowedConfig: AccessPolicy['allowedConfig'] | undefined;
}): string | undefined {
  if (!user) {
    // no user, only allowed to access 'no-user' routes
    if (accessFlag !== 'no-user') {
      console.warn('Trying to access USER route without user', user, match);
      return LOGIN_PATH;
    }

    return;
  }

  if (accessFlag === 'no-user') {
    console.warn('NO-USER-Route has user', user, match);
    return '/';
  }

  if (accessFlag === 'restrict') {
    if (isModuleRestricted(user, match.pathname, allowedConfig)) {
      console.warn('Trying to access RESTRICT route', user, match);
      return '/';
    }
  }
}

// ─── main guard ─────────────────────────────────────────────────────────────

/**
 * Walks the matched route stack and returns a redirect path if the user is not
 * allowed to access any segment, or undefined if access is granted.
 *
 * - Role entries (user, partner, salesAdmin, admin) use OR semantics
 * - Special entries (no-user, restrict) use AND semantics
 * - 'all' skips all checks for that segment
 */
export function resolveRouteAccessRedirect(
  user: User | null | undefined,
  matches: RouteMatch[],
): string | undefined {
  for (const match of matches) {
    const { access } = match.handle ?? {};

    const allowed = access?.allowed;
    const allowedConfig = access?.allowedConfig;

    if (!allowed) {
      continue;
    }

    // 'all' means public — skip all further checks for this route segment
    if (allowed.includes('all')) continue;

    // Allowed contains flags and roles, so separate them to handle separately
    const allowedRoles = allowed.filter((item): item is AllowedRole =>
      (ROLE_ITEMS as readonly string[]).includes(item),
    );
    const accessFlags = allowed.filter(
      (item) => !(ROLE_ITEMS as readonly string[]).includes(item),
    );

    // Access flags use AND semantics and are evaluated individually.
    for (const accessFlag of accessFlags) {
      const redirect = getAccessFlagRedirect({
        accessFlag,
        user,
        match,
        allowedConfig,
      });

      if (redirect) {
        return redirect;
      }
    }

    // Roles use OR semantics: the user must satisfy at least one required role.
    // Unauthenticated users are redirected to login if any role is required.
    if (allowedRoles.length > 0) {
      if (!user) {
        return LOGIN_PATH;
      }

      const hasAccess = allowedRoles.some((role) =>
        userSatisfiesRole(user, role),
      );
      if (!hasAccess) {
        return '/';
      }
    }
  }
}
